import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { trackEvent } from '../lib/analytics';
import {
  ArrowRight,
  Sparkles,
  Phone,
  Mail,
  MessageCircle,
  Briefcase,
  Megaphone,
  PenTool,
  Calendar,
  Plug,
  Workflow,
  Zap,
  CheckCircle2,
  Clock,
  TrendingUp,
  Users,
  ShieldCheck,
  PlayCircle,
  X as XIcon,
  Bot,
  ChevronRight,
  ArrowUpRight,
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import SEO from '../components/SEO';
import ContinueExploring from '../components/ContinueExploring';
import { AffiliateStrip } from '../components/AffiliateStrip';
import { ProductPricingCTA, FlagshipStrip } from '../components/FlagshipFunnel';
import { useT } from '../i18n';

/* =================================================================
   AI Agents for Restaurants — flagship product page.
   Premium SaaS layout: hero → rollout workflow → AI team grid →
   embedded demo video → key capabilities + benefits → compatible
   platforms → managed infrastructure → outcomes → final CTA.
   ================================================================= */

// ---------- Bilingual content ----------
const COPY = {
  en: {
    eyebrow: 'DIGITAL WORKFORCE FOR RESTAURANTS',
    heroTitle: 'Right now, your restaurant is missing calls, reviews and orders. An AI workforce never would.',
    heroBody: "While you cook, guests call, message and book — and if nobody answers in minutes, they simply go to the next restaurant on the list. Your Kitxens AI agents answer every single one, sell for you, and work 24/7 — even when you're closed.",
    heroSub: "Your Kitxens AI Team works like a real digital team that never sleeps, never misses a lead, and keeps getting better the longer they work for you. The restaurants that deploy it first take the guests — the rest wonder where they went.",
    heroCtaA: 'Build My AI Team',
    heroCtaB: 'See How It Works',

    rolloutEyebrow: 'HOW IT WORKS',
    rolloutTitle: 'A complete AI system that runs your restaurant. Ready to work.',
    rolloutBody: 'We deploy your AI workforce in days, connecting directly with your restaurant operations, systems and customer channels.',
    rollout: [
      { i: Plug, t: 'Connect', b: 'We integrate Kitxens with your POS, ordering, reservations, CRM and review platforms.' },
      { i: Bot, t: 'Deploy', b: 'Your AI agents are trained on your menu, voice, policies and team workflows.' },
      { i: Zap, t: 'Activate', b: 'They go live across phone, web, email, social and chat — supervised by a Kitxens human lead.' },
      { i: TrendingUp, t: 'Optimize', b: 'Weekly tuning + monthly reviews so the team gets sharper every cycle.' },
    ],

    teamEyebrow: 'MEET YOUR AI TEAM',
    teamTitle: 'Hire a digital workforce. Not another tool.',
    teamBody: 'Every agent is built for restaurants, fluent in EN and ES, and trained on the way your brand talks to guests.',
    teamFooter: 'More agents are joining the team every quarter — Bob (Finance Analyst), Mike (Sales) and others.',

    keyEyebrow: 'AI OPERATIONS ENGINE',
    keyTitle: 'What your AI team does every single day.',
    keyBody: 'A complete digital workforce that runs sales, marketing and operations in the background — while you focus on guests and growth.',
    capabilitiesTitle: 'Key capabilities',
    capabilities: [
      { i: Phone, t: 'Answer calls' },
      { i: Calendar, t: 'Take reservations' },
      { i: Briefcase, t: 'Capture leads' },
      { i: MessageCircle, t: 'Follow up customers' },
      { i: Mail, t: 'Manage inbox' },
      { i: Megaphone, t: 'Create social content' },
      { i: PenTool, t: 'Write SEO content' },
      { i: TrendingUp, t: 'Generate reports' },
      { i: Workflow, t: 'Coordinate operations' },
      { i: Users, t: 'Support your staff' },
    ],
    benefitsTitle: 'Business benefits',
    benefits: [
      { i: Clock, t: 'Available 24/7' },
      { i: Zap, t: 'Never miss a lead' },
      { i: TrendingUp, t: 'Lower operating costs' },
      { i: Calendar, t: 'More reservations' },
      { i: Sparkles, t: 'Better guest experience' },
      { i: Users, t: 'More qualified leads' },
      { i: Megaphone, t: 'Consistent marketing' },
      { i: ShieldCheck, t: 'Scalable growth' },
    ],

    videoEyebrow: 'WATCH THE 90-SECOND DEMO',
    videoTitle: 'See your AI team in action.',
    videoBody: "From the first phone call to the final social post — this is what 'never miss a lead' actually looks like.",

    partnersEyebrow: 'TRUSTED ECOSYSTEM',
    partnersTitle: 'Built on the technology stack restaurants already trust.',

    marblismEyebrow: 'MANAGED BY KITXENS',
    marblismTitle: 'Enterprise-grade AI infrastructure. Fully managed for you.',
    marblismBody: 'Your AI workforce runs on the same orchestration layer used by Fortune-500 enterprises — deployed, monitored and continuously improved by KITXENS. You never touch the technology. You simply see the results.',
    marblismBullets: [
      'Production-grade agent orchestration',
      'Enterprise security and data isolation',
      'Best-in-class AI models, always up to date',
      'Continuous evaluation and observability',
    ],
    marblismCta: 'See how we manage it',

    outcomesEyebrow: 'MEASURABLE OUTCOMES',
    outcomes: [
      { k: '100%', v: 'Call coverage, 24/7' },
      { k: '+30%', v: 'More qualified reservations in 90 days' },
      { k: '20+ hr', v: 'Saved per week across operations' },
      { k: '< 2 hr', v: 'Average review reply time' },
    ],

    ctaTitle: 'Your AI workforce is ready. Today.',
    ctaBody: 'No hiring. No training. No management overhead. Activate your team in days.',
    ctaA: 'Activate My AI Team',
    ctaB: 'Talk to an expert',
  },
  es: {
    eyebrow: 'EQUIPO DIGITAL PARA RESTAURANTES',
    heroTitle: 'Ahora mismo tu restaurante está perdiendo llamadas, reseñas y pedidos. Un equipo de IA jamás los perdería.',
    heroBody: 'Mientras cocinas, los clientes llaman, escriben y reservan — y si nadie responde en minutos, se van al siguiente restaurante de la lista. Tus agentes de IA de Kitxens responden a todos, venden por ti y trabajan 24/7 — incluso cuando cierras.',
    heroSub: 'Tu AI Team de Kitxens funciona como un equipo digital real: nunca duermen, no pierden leads y mejoran cuanto más trabajan para ti. Los restaurantes que lo activan primero se llevan a los clientes — el resto se pregunta a dónde se fueron.',
    heroCtaA: 'Arma mi equipo IA',
    heroCtaB: 'Cómo funciona',

    rolloutEyebrow: 'CÓMO FUNCIONA',
    rolloutTitle: 'Un sistema completo de IA que opera tu restaurante. Listo para trabajar.',
    rolloutBody: 'Desplegamos tu equipo de IA en días, conectando directo con tu operación, sistemas y canales con el cliente.',
    rollout: [
      { i: Plug, t: 'Conecta', b: 'Integramos Kitxens con tu POS, pedidos, reservas, CRM y reseñas.' },
      { i: Bot, t: 'Despliega', b: 'Tus agentes IA se entrenan con tu menú, voz, políticas y workflows.' },
      { i: Zap, t: 'Activa', b: 'Salen en vivo en teléfono, web, email, social y chat — con supervisión humana.' },
      { i: TrendingUp, t: 'Optimiza', b: 'Ajuste semanal + revisión mensual; el equipo se afila ciclo a ciclo.' },
    ],

    teamEyebrow: 'CONOCE A TU EQUIPO IA',
    teamTitle: 'Contrata un equipo digital. No otra herramienta.',
    teamBody: 'Cada agente está construido para restaurantes, es bilingüe EN/ES y se entrena con el tono de tu marca.',
    teamFooter: 'Más agentes se suman al equipo cada trimestre — Bob (Analista Financiero), Mike (Ventas) y más.',

    keyEyebrow: 'MOTOR DE OPERACIONES IA',
    keyTitle: 'Lo que tu equipo IA hace cada día.',
    keyBody: 'Un equipo digital completo que corre ventas, marketing y operaciones en segundo plano — mientras tú te enfocas en clientes y crecimiento.',
    capabilitiesTitle: 'Capacidades clave',
    capabilities: [
      { i: Phone, t: 'Responde llamadas' },
      { i: Calendar, t: 'Toma reservas' },
      { i: Briefcase, t: 'Captura leads' },
      { i: MessageCircle, t: 'Follow-up de clientes' },
      { i: Mail, t: 'Gestiona el inbox' },
      { i: Megaphone, t: 'Crea contenido social' },
      { i: PenTool, t: 'Escribe contenido SEO' },
      { i: TrendingUp, t: 'Genera reportes' },
      { i: Workflow, t: 'Coordina operaciones' },
      { i: Users, t: 'Apoya a tu equipo' },
    ],
    benefitsTitle: 'Beneficios de negocio',
    benefits: [
      { i: Clock, t: 'Disponible 24/7' },
      { i: Zap, t: 'Nunca pierde un lead' },
      { i: TrendingUp, t: 'Menor costo operativo' },
      { i: Calendar, t: 'Más reservas' },
      { i: Sparkles, t: 'Mejor experiencia para el cliente' },
      { i: Users, t: 'Más leads calificados' },
      { i: Megaphone, t: 'Marketing consistente' },
      { i: ShieldCheck, t: 'Crecimiento escalable' },
    ],

    videoEyebrow: 'MIRA EL DEMO DE 90 SEGUNDOS',
    videoTitle: 'Ve a tu equipo IA en acción.',
    videoBody: 'De la primera llamada al último post de redes — así se ve realmente "no perder un lead".',

    partnersEyebrow: 'ECOSISTEMA DE CONFIANZA',
    partnersTitle: 'Construido sobre el stack que los restaurantes ya confían.',

    marblismEyebrow: 'GESTIONADO POR KITXENS',
    marblismTitle: 'Infraestructura IA de nivel enterprise. Totalmente gestionada.',
    marblismBody: 'Tu equipo de IA opera sobre la misma capa de orquestación que usan empresas Fortune-500 — desplegada, monitoreada y mejorada continuamente por KITXENS. Tú nunca tocas la tecnología. Solo ves los resultados.',
    marblismBullets: [
      'Orquestación de agentes nivel producción',
      'Seguridad enterprise y aislamiento de datos',
      'Los mejores modelos de IA, siempre actualizados',
      'Evaluación continua y observabilidad',
    ],
    marblismCta: 'Mira cómo lo gestionamos',

    outcomesEyebrow: 'RESULTADOS MEDIBLES',
    outcomes: [
      { k: '100%', v: 'Cobertura de llamadas 24/7' },
      { k: '+30%', v: 'Más reservas calificadas en 90 días' },
      { k: '20+ hr', v: 'Ahorradas por semana en operación' },
      { k: '< 2 hr', v: 'Tiempo promedio de respuesta a reseñas' },
    ],

    ctaTitle: 'Tu equipo IA está listo. Hoy.',
    ctaBody: 'Sin contratación. Sin entrenamiento. Sin overhead. Activa tu equipo en días.',
    ctaA: 'Activar mi equipo IA',
    ctaB: 'Hablar con un experto',
  },
};

// ---------- AI Team data (avatars on Wix CDN as the legacy investment) ----------
const MARBLISM_URL = 'https://marblism.com?via=kitxens-com';
const API_BASE = process.env.REACT_APP_BACKEND_URL;

/* Each AI executive signs real webzine pieces — mini-bios + their latest signed articles. */
function AuthorsShowcase({ lang }) {
  const isEs = lang === 'es';
  const [authors, setAuthors] = useState([]);
  const [arts, setArts] = useState({});
  useEffect(() => {
    fetch(`${API_BASE}/api/magazine/authors`)
      .then((r) => r.json())
      .then(async (list) => {
        const ai = (list || []).filter((a) => a.role === 'ai' && a.article_count > 0);
        setAuthors(ai);
        const out = {};
        await Promise.all(ai.map(async (a) => {
          try {
            const j = await (await fetch(`${API_BASE}/api/magazine/articles?author=${a.slug}&page_size=3`)).json();
            out[a.slug] = j.items || [];
          } catch { out[a.slug] = []; }
        }));
        setArts(out);
      })
      .catch(() => {});
  }, []);
  if (!authors.length) return null;
  return (
    <section className="py-20 lg:py-24 bg-[#FAFAF8]" data-testid="ai-authors-showcase">
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
        <span className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 text-[#F77F11] text-[11px] tracking-[0.22em] font-semibold uppercase px-3 py-1.5 rounded-full">
          <PenTool className="w-3.5 h-3.5" />
          {isEs ? 'Ellos también escriben' : 'They also write'}
        </span>
        <h2 className="mt-6 text-3xl sm:text-4xl font-bold tracking-[-0.02em] text-neutral-900 leading-[1.1] max-w-2xl">
          {isEs ? 'Cada ejecutivo IA firma lo que sabe en la Kitxens Webzine' : 'Each AI executive signs what they know in the Kitxens Webzine'}
        </h2>
        <p className="mt-4 text-neutral-500 text-[15px] max-w-2xl leading-relaxed">
          {isEs
            ? 'No son avatares decorativos: Stan escribe de ventas, Linda de márgenes, Sonny de visibilidad. Lo que publican es lo mismo que aplican cada día en los restaurantes que gestionamos.'
            : 'They are not decorative avatars: Stan writes about sales, Linda about margins, Sonny about visibility. What they publish is what they apply daily in the restaurants we run.'}
        </p>
        <div className="mt-10 grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {authors.map((a) => (
            <div key={a.slug} data-testid={`ai-author-card-${a.slug}`}
              className="rounded-3xl border border-neutral-200 bg-white p-6 hover:border-[#F77F11]/60 hover:shadow-[0_24px_60px_-30px_rgba(247,127,17,0.35)] transition-all">
              <div className="flex items-center gap-3.5">
                <img src={a.avatar_url} alt={a.name} className="w-14 h-14 rounded-2xl object-cover ring-2 ring-orange-100" loading="lazy" />
                <div>
                  <p className="font-bold text-[16px] text-neutral-900">{a.name}</p>
                  <p className="text-[11.5px] font-semibold text-[#F77F11] uppercase tracking-wide">{a.title}</p>
                </div>
                <span className="ml-auto rounded-full bg-neutral-100 text-neutral-500 text-[10.5px] font-bold px-2 py-0.5">
                  {a.article_count} {isEs ? 'artículos' : 'articles'}
                </span>
              </div>
              <p className="mt-3.5 text-[13px] text-neutral-500 leading-relaxed line-clamp-3">{a.bio}</p>
              {(arts[a.slug] || []).length > 0 && (
                <ul className="mt-4 space-y-2 border-t border-neutral-100 pt-4">
                  {(arts[a.slug] || []).map((art) => (
                    <li key={art.slug}>
                      <Link to={`/magazine/${art.slug}?utm_source=ai_agents_page&utm_medium=author_showcase`}
                        data-testid={`ai-author-article-${art.slug}`}
                        className="group flex items-start gap-2 text-[13px] text-neutral-700 hover:text-[#F77F11] transition-colors">
                        <ArrowRight className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#F77F11] opacity-60 group-hover:opacity-100" />
                        <span className="font-medium leading-snug line-clamp-2">{art.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              <Link to={`/magazine/author/${a.slug}`} data-testid={`ai-author-all-${a.slug}`}
                className="mt-4 inline-flex items-center gap-1 text-[12px] font-bold text-[#F77F11] hover:gap-2 transition-all">
                {isEs ? `Todo lo de ${a.name}` : `Everything by ${a.name}`} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ))}
        </div>
        {/* Partner callout — always with the Kitxens partner link */}
        <div className="mt-10 rounded-3xl border border-neutral-200 bg-white p-6 sm:p-7 flex flex-wrap items-center gap-4 justify-between" data-testid="marblism-partner-callout">
          <div className="max-w-xl">
            <p className="text-[11px] tracking-[0.22em] uppercase font-bold text-neutral-400">{isEs ? 'Tecnología de empleados IA' : 'AI employee technology'}</p>
            <p className="mt-1.5 text-[14.5px] text-neutral-700 leading-relaxed">
              {isEs
                ? 'Nuestros ejecutivos IA se construyen sobre Marblism, la plataforma de empleados IA que usamos y recomendamos como partners.'
                : 'Our AI executives are built on Marblism, the AI-employee platform we use and recommend as partners.'}
            </p>
          </div>
          <a href={MARBLISM_URL} target="_blank" rel="sponsored noopener noreferrer" data-testid="marblism-partner-link"
            onClick={() => trackEvent('partner_click', { kind: 'marblism', url: MARBLISM_URL })}
            className="inline-flex items-center gap-2 rounded-xl bg-neutral-900 hover:bg-neutral-700 text-white font-bold text-[13.5px] px-5 py-3 transition-colors">
            {isEs ? 'Descubre Marblism' : 'Discover Marblism'} <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

const TEAM = [
  {
    key: 'rachel',
    img: 'https://static.wixstatic.com/media/c99602_6f8807f1e90e41a3b24d7e72813e945b~mv2.png/v1/fill/w_400,h_400,al_c,q_85,enc_avif,quality_auto/Rachel%20AI%20Receptiionist.png',
    icon: Phone,
    metric: '100%',
    en: {
      name: 'Rachel',
      role: 'AI Receptionist',
      problem: 'Missed calls become missed reservations.',
      duties: 'Answers calls, handles bookings and provides instant guest support 24/7 — in your brand voice and in EN or ES.',
      value: 'Zero missed calls. Every booking captured.',
      metricLabel: 'Call coverage 24/7',
      integrations: ['Twilio', 'OpenTable', 'Toast', 'Square'],
    },
    es: {
      name: 'Rachel',
      role: 'Recepcionista IA',
      problem: 'Llamadas perdidas = reservas perdidas.',
      duties: 'Responde llamadas, gestiona reservas y soporte al cliente 24/7 — con tu voz de marca, en EN o ES.',
      value: 'Cero llamadas perdidas. Cada reserva capturada.',
      metricLabel: 'Cobertura de llamadas 24/7',
      integrations: ['Twilio', 'OpenTable', 'Toast', 'Square'],
    },
  },
  {
    key: 'stan',
    img: 'https://static.wixstatic.com/media/c99602_34b2b413997d4d7c8356b03425fc0a3f~mv2.png/v1/fill/w_400,h_400,al_c,q_85,enc_avif,quality_auto/stan.png',
    icon: Briefcase,
    metric: '+45%',
    en: {
      name: 'Stan',
      role: 'Lead Generation Agent',
      problem: 'Catering and corporate revenue stays flat without outbound.',
      duties: 'Finds new corporate and catering leads, qualifies prospects and books opportunities directly into your calendar.',
      value: 'A predictable pipeline of high-value buyers.',
      metricLabel: 'Catering revenue in 6 months',
      integrations: ['HubSpot', 'LinkedIn', 'Calendly', 'Gmail'],
    },
    es: {
      name: 'Stan',
      role: 'Generador de Leads',
      problem: 'Catering y corporativo no crecen sin outbound.',
      duties: 'Encuentra leads corporativos y de catering, califica prospectos y agenda oportunidades en tu calendario.',
      value: 'Pipeline predecible de compradores de alto valor.',
      metricLabel: 'Ingresos de catering a 6 meses',
      integrations: ['HubSpot', 'LinkedIn', 'Calendly', 'Gmail'],
    },
  },
  {
    key: 'eva',
    img: '/avatars/eva.png',
    icon: Calendar,
    metric: '20+ hr',
    en: {
      name: 'Eva',
      role: 'Operations Manager',
      problem: 'Owners burn nights on inbox, suppliers and shift logistics.',
      duties: 'Manages your schedule, inbox, supplier follow-ups and daily operational tasks — keeps the team moving.',
      value: 'Hours back in your week, every week.',
      metricLabel: 'Saved per week',
      integrations: ['Gmail', 'Google Calendar', 'Slack', 'Notion'],
    },
    es: {
      name: 'Eva',
      role: 'Gerente de Operaciones',
      problem: 'Los dueños queman noches en inbox, proveedores y turnos.',
      duties: 'Gestiona tu agenda, inbox, follow-ups de proveedores y tareas diarias — mantiene al equipo en movimiento.',
      value: 'Horas recuperadas a tu semana, cada semana.',
      metricLabel: 'Ahorradas por semana',
      integrations: ['Gmail', 'Google Calendar', 'Slack', 'Notion'],
    },
  },
  {
    key: 'sonny',
    img: 'https://static.wixstatic.com/media/c99602_3a81557ac6c64259844ee85fdd2dbcb1~mv2.png/v1/fill/w_400,h_400,al_c,q_85,enc_avif,quality_auto/sonny.png',
    icon: Megaphone,
    metric: '5×',
    en: {
      name: 'Sonny',
      role: 'Social Media Manager',
      problem: 'Restaurants disappear from social when staff get busy.',
      duties: 'Creates content, posts on schedule, engages followers and grows your brand across Instagram, Facebook and TikTok.',
      value: 'A consistent social presence — without the agency fee.',
      metricLabel: 'Content output vs. in-house',
      integrations: ['Instagram', 'Facebook', 'TikTok', 'X'],
    },
    es: {
      name: 'Sonny',
      role: 'Manager de Redes',
      problem: 'Los restaurantes desaparecen de redes cuando el equipo se ocupa.',
      duties: 'Crea contenido, publica en agenda, interactúa con seguidores y crece tu marca en Instagram, Facebook y TikTok.',
      value: 'Presencia social consistente — sin tarifa de agencia.',
      metricLabel: 'Volumen vs. equipo interno',
      integrations: ['Instagram', 'Facebook', 'TikTok', 'X'],
    },
  },
  {
    key: 'penny',
    img: 'https://static.wixstatic.com/media/c99602_8e0e2a1e5e874e46b3afd88a1fde14cd~mv2.png/v1/fill/w_400,h_400,al_c,q_85,enc_avif,quality_auto/Penny.png',
    icon: PenTool,
    metric: '+60%',
    en: {
      name: 'Penny',
      role: 'SEO Content Writer',
      problem: "You aren't showing up for the local searches that actually matter.",
      duties: 'Writes SEO content that ranks, attracts local traffic and builds long-term search authority for your restaurant.',
      value: 'Compounding organic visibility, month after month.',
      metricLabel: 'Organic traffic in 90 days',
      integrations: ['WordPress', 'Webflow', 'Google Search Console', 'Index AI'],
    },
    es: {
      name: 'Penny',
      role: 'Escritora SEO',
      problem: 'No apareces en las búsquedas locales que importan.',
      duties: 'Escribe contenido SEO que rankea, atrae tráfico local y construye autoridad a largo plazo.',
      value: 'Visibilidad orgánica que se compone, mes tras mes.',
      metricLabel: 'Tráfico orgánico a 90 días',
      integrations: ['WordPress', 'Webflow', 'Google Search Console', 'Index AI'],
    },
  },
  {
    key: 'linda',
    img: '/avatars/linda.png',
    icon: ShieldCheck,
    metric: '< 24 hr',
    en: {
      name: 'Linda',
      role: 'Legal Assistant',
      problem: 'Restaurants sign supplier and lease contracts they never properly review.',
      duties: 'Reviews supplier agreements, lease addenda, employee policies and compliance docs — flags risk before you sign.',
      value: 'Faster, safer contracts. Fewer surprises on the back end.',
      metricLabel: 'Contract review turnaround',
      integrations: ['Google Drive', 'DocuSign', 'Notion', 'Gmail'],
    },
    es: {
      name: 'Linda',
      role: 'Asistente Legal',
      problem: 'Los restaurantes firman contratos de proveedores y locales sin revisarlos bien.',
      duties: 'Revisa contratos de proveedores, anexos de locales, políticas de empleados y compliance — marca riesgo antes de firmar.',
      value: 'Contratos más rápidos y seguros. Menos sorpresas.',
      metricLabel: 'Tiempo de revisión de contrato',
      integrations: ['Google Drive', 'DocuSign', 'Notion', 'Gmail'],
    },
  },
];

// ---------- Trusted ecosystem (text-badge strip) ----------
const ECOSYSTEM = ['Twilio', 'HubSpot', 'Google', 'Meta', 'Toast', 'Square', 'OpenTable'];

/* ===================== Page ===================== */
export default function AiAgentsPage() {
  const { lang } = useT();
  const c = COPY[lang] || COPY.en;
  const [videoOpen, setVideoOpen] = useState(false);

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.kitxens.com/' },
          { '@type': 'ListItem', position: 2, name: 'AI Solutions', item: 'https://www.kitxens.com/index-ai' },
          { '@type': 'ListItem', position: 3, name: 'AI Agents for Restaurants', item: 'https://www.kitxens.com/ai-agents-for-restaurants' },
        ],
      },
      {
        '@type': 'Service',
        '@id': 'https://www.kitxens.com/ai-agents-for-restaurants#service',
        name: 'AI Agents for Restaurants',
        serviceType: 'AI Solutions',
        additionalType: 'https://schema.org/SoftwareApplication',
        description: c.heroBody,
        provider: { '@type': 'Organization', name: 'Kitxens', url: 'https://www.kitxens.com' },
        url: 'https://www.kitxens.com/ai-agents-for-restaurants',
      },
    ],
  };

  return (
    <div className="bg-white" data-testid="ai-agents-page">
      <SEO
        title="AI Receptionist & AI Agents for Restaurants — Never Miss a Call"
        description={c.heroBody}
        path="/ai-agents-for-restaurants"
        schema={schema}
      />

      {/* ========== 1. Hero ========== */}
      <section className="relative pt-28 pb-16 lg:pt-32 lg:pb-24 overflow-hidden bg-gradient-to-b from-[#0F1117] via-[#0F1117] to-[#1A1D26] text-white">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.18] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 18% 22%, #F77F11 1px, transparent 1.5px), radial-gradient(circle at 80% 70%, #F77F11 1px, transparent 1.5px)',
            backgroundSize: '110px 110px, 160px 160px',
          }}
        />
        <div
          aria-hidden="true"
          className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full opacity-30 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(247,127,17,0.35), transparent 65%)' }}
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-40 -right-40 w-[640px] h-[640px] rounded-full opacity-25 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(247,127,17,0.25), transparent 70%)' }}
        />

        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="lg:col-span-7"
          >
            <span className="inline-flex items-center gap-2 bg-[#F77F11]/15 border border-[#F77F11]/30 text-[#FFB672] text-[11px] tracking-[0.22em] font-semibold uppercase px-3 py-1.5 rounded-full">
              <Sparkles className="w-3.5 h-3.5" /> {c.eyebrow}
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-[60px] font-bold leading-[1.05] tracking-[-0.025em]">
              {c.heroTitle}
            </h1>
            <p className="mt-6 text-neutral-300 text-base sm:text-lg leading-relaxed max-w-2xl">
              {c.heroBody}
            </p>
            <p className="mt-4 text-neutral-400 text-[14.5px] leading-relaxed max-w-2xl">
              {c.heroSub}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link
                to="/pricing"
                className="inline-flex items-center gap-1 bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 px-7 rounded-full text-[15px] font-semibold shadow-[0_15px_40px_-15px_rgba(247,127,17,0.6)] hover:shadow-[0_20px_50px_-15px_rgba(247,127,17,0.7)] transition-all"
                data-testid="ai-agents-cta-primary"
              >
                {c.heroCtaA} <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
              <Link
                to="/how-it-works"
                className="inline-flex items-center gap-1 border border-white/25 hover:border-[#F77F11] hover:text-[#F77F11] text-white h-12 px-7 rounded-full text-[15px] font-semibold transition-colors"
              >
                {c.heroCtaB} <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
            {/* Inline trust pills */}
            <div className="mt-9 flex flex-wrap gap-x-6 gap-y-3 text-[12.5px] text-neutral-400">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#F77F11]" /> {lang === 'es' ? '24/7 EN / ES' : '24/7 EN / ES'}</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#F77F11]" /> {lang === 'es' ? 'Sin costo de setup' : 'No setup fees'}</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#F77F11]" /> {lang === 'es' ? 'Desplegado en días' : 'Deployed in days'}</span>
            </div>
          </motion.div>

          {/* Right: floating agent avatars + team illustration */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative rounded-3xl bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/10 backdrop-blur-md p-6 sm:p-7 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.6)]">
              <div className="flex items-center justify-between mb-5">
                <p className="text-[11px] tracking-[0.22em] font-semibold uppercase text-[#FFB672]">
                  {lang === 'es' ? 'Equipo en vivo' : 'Live team'}
                </p>
                <span className="inline-flex items-center gap-1.5 text-[10.5px] text-emerald-300 font-semibold">
                  <span className="relative flex w-2 h-2">
                    <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-70" />
                    <span className="relative w-2 h-2 rounded-full bg-emerald-400" />
                  </span>
                  {lang === 'es' ? 'Operando' : 'Operating'}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {TEAM.map((m, i) => {
                  const I = m.icon;
                  const t = m[lang] || m.en;
                  return (
                    <motion.div
                      key={m.key}
                      id={m.key}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 + i * 0.06, duration: 0.4 }}
                      className="group flex flex-col items-center text-center"
                    >
                      <div className="relative">
                        <div className="w-16 h-16 rounded-full overflow-hidden bg-gradient-to-br from-[#F77F11]/30 to-[#F77F11]/10 ring-2 ring-white/10 group-hover:ring-[#F77F11]/50 transition-all">
                          <img
                            src={m.img}
                            alt={`${t.name} — ${t.role}`}
                            loading="lazy"
                            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.classList.add('flex','items-center','justify-center'); }}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#0F1117] border border-[#F77F11]/60 flex items-center justify-center">
                          <I className="w-3 h-3 text-[#F77F11]" />
                        </span>
                      </div>
                      <p className="mt-2.5 text-white text-[12px] font-semibold tracking-[-0.005em]">{t.name}</p>
                      <p className="text-[10px] text-neutral-400 leading-tight px-1">{t.role}</p>
                    </motion.div>
                  );
                })}
              </div>
              <div className="mt-5 flex items-center justify-between text-[11.5px] text-neutral-400 border-t border-white/10 pt-4">
                <span>{lang === 'es' ? '6 agentes desplegados' : '6 agents deployed'}</span>
                <span className="inline-flex items-center gap-1 text-[#FFB672] font-semibold">
                  {lang === 'es' ? 'Más en camino' : 'More on the way'} <ChevronRight className="w-3 h-3" />
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ========== 2. Rollout workflow ========== */}
      <section className="py-20 lg:py-24" data-testid="ai-agents-rollout">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-semibold uppercase">{c.rolloutEyebrow}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-[42px] font-bold text-neutral-900 tracking-[-0.02em] leading-[1.1]">{c.rolloutTitle}</h2>
            <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed">{c.rolloutBody}</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 relative">
            {c.rollout.map((s, i) => {
              const I = s.i;
              return (
                <motion.div
                  key={s.t}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="group relative rounded-2xl border border-neutral-200 bg-white p-6 hover:border-orange-200 hover:shadow-[0_24px_60px_-30px_rgba(247,127,17,0.4)] transition-all duration-300"
                >
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-orange-50 group-hover:bg-[#F77F11] transition-colors duration-300">
                      <I className="w-5 h-5 text-[#F77F11] group-hover:text-white transition-colors duration-300" />
                    </span>
                    <span className="text-[11px] tracking-[0.22em] font-bold text-neutral-300">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <h3 className="mt-5 text-neutral-900 font-semibold text-[17px] tracking-[-0.005em]">{s.t}</h3>
                  <p className="mt-2 text-neutral-600 text-[14.5px] leading-relaxed">{s.b}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== 3. Meet the AI Team ========== */}
      <section className="py-20 lg:py-24 bg-neutral-50/60" data-testid="ai-agents-team">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-semibold uppercase">{c.teamEyebrow}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-[42px] font-bold text-neutral-900 tracking-[-0.02em] leading-[1.1]">{c.teamTitle}</h2>
            <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed">{c.teamBody}</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {TEAM.map((m, i) => {
              const I = m.icon;
              const t = m[lang] || m.en;
              return (
                <motion.div
                  key={m.key}
                  id={m.key}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="group h-full scroll-mt-28"
                  data-testid={`ai-agent-card-${m.key}`}
                >
                  <div className="h-full rounded-3xl border border-neutral-200 bg-white p-7 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:border-[#F77F11] hover:shadow-[0_30px_70px_-30px_rgba(247,127,17,0.4)]">
                    {/* Top: Avatar + identity */}
                    <div className="flex items-start gap-4">
                      <div className="relative shrink-0">
                        <div className="w-20 h-20 rounded-2xl overflow-hidden bg-gradient-to-br from-orange-50 to-orange-100 ring-1 ring-orange-200/60">
                          <img
                            src={m.img}
                            alt={`${t.name} — ${t.role}`}
                            loading="lazy"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            className="w-full h-full object-cover scale-[1.02] group-hover:scale-105 transition-transform duration-500 ease-out"
                          />
                        </div>
                        <span className="absolute -bottom-1.5 -right-1.5 w-8 h-8 rounded-full bg-[#F77F11] flex items-center justify-center shadow-md">
                          <I className="w-4 h-4 text-white" />
                        </span>
                      </div>
                      <div className="flex-1 min-w-0 pt-1">
                        <h3 className="text-xl font-semibold text-neutral-900 tracking-[-0.01em]">{t.name}</h3>
                        <p className="mt-1 text-[#F77F11] text-[12px] font-semibold tracking-[0.08em] uppercase">{t.role}</p>
                      </div>
                    </div>

                    {/* Problem framing */}
                    <p className="mt-5 text-[12.5px] font-medium text-neutral-500 italic leading-snug">
                      “{t.problem}”
                    </p>

                    {/* What they actually do */}
                    <p className="mt-3 text-neutral-700 text-[14.5px] leading-relaxed">{t.duties}</p>

                    {/* Metric strip */}
                    <div className="mt-5 pt-5 border-t border-neutral-100 flex items-baseline gap-3">
                      <span className="text-[#F77F11] text-[28px] font-bold tracking-[-0.025em] leading-none">{m.metric}</span>
                      <span className="text-[11.5px] text-neutral-500 leading-snug">{t.metricLabel}</span>
                    </div>

                    {/* Business value */}
                    <p className="mt-4 text-neutral-900 text-[14px] font-semibold leading-snug">{t.value}</p>

                    {/* Integrations */}
                    <div className="mt-5 flex-1 flex flex-col justify-end">
                      <p className="text-[10px] tracking-[0.22em] font-semibold uppercase text-neutral-500 mb-2">
                        {lang === 'es' ? 'Integraciones' : 'Integrations'}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {t.integrations.map((it) => (
                          <span key={it} className="text-[11.5px] font-medium text-neutral-700 bg-neutral-100 group-hover:bg-orange-50 group-hover:text-[#F77F11] border border-neutral-200 group-hover:border-orange-200 rounded-full px-2.5 py-1 transition-colors">
                            {it}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Footer line — future agents mention */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.4 }}
            className="mt-10 text-center text-neutral-500 text-[14px]"
          >
            <Sparkles className="inline w-4 h-4 text-[#F77F11] mr-2 align-text-bottom" />
            {c.teamFooter}
          </motion.p>
        </div>
      </section>

      {/* ========== 4. Demo video ========== */}
      <section className="py-20 lg:py-24" data-testid="ai-agents-video">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-10">
            <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-semibold uppercase">{c.videoEyebrow}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-[42px] font-bold text-neutral-900 tracking-[-0.02em] leading-[1.1]">{c.videoTitle}</h2>
            <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed">{c.videoBody}</p>
          </div>

          <motion.button
            type="button"
            onClick={() => setVideoOpen(true)}
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5 }}
            className="group relative w-full aspect-[16/9] rounded-3xl overflow-hidden bg-[#0F1117] shadow-[0_50px_120px_-40px_rgba(15,17,23,0.55)] hover:shadow-[0_60px_140px_-40px_rgba(247,127,17,0.45)] transition-shadow duration-500"
            aria-label="Play demo video"
            data-testid="ai-agents-video-trigger"
          >
            <img
              src="https://static.wixstatic.com/media/c99602_ed21ba4ca7ca419f8fcecf702bcfc904~mv2.png/v1/fill/w_1400,h_788,al_c,q_90,enc_avif,quality_auto/yourAITeam.png"
              alt="Kitxens AI Team — your digital workforce"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
              className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-75 group-hover:scale-[1.02] transition-all duration-700 ease-out"
            />
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-tr from-[#0F1117]/85 via-[#0F1117]/50 to-[#F77F11]/20"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex items-center gap-4 bg-white/95 backdrop-blur px-7 py-4 rounded-full shadow-2xl group-hover:scale-105 transition-transform duration-300">
                <PlayCircle className="w-8 h-8 text-[#F77F11]" />
                <span className="text-neutral-900 font-semibold text-[15px]">
                  {lang === 'es' ? 'Ver demo · 90 s' : 'Watch demo · 90s'}
                </span>
              </div>
            </div>
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4 text-white">
              <div>
                <p className="text-[10px] tracking-[0.22em] font-bold uppercase text-[#FFB672]">{lang === 'es' ? 'En vivo' : 'Live'}</p>
                <p className="mt-1 text-lg font-semibold tracking-[-0.01em]">{lang === 'es' ? 'Kitxens AI Team en acción' : 'Kitxens AI Team in action'}</p>
              </div>
              <span className="hidden sm:inline-flex items-center gap-2 text-[12px] text-white/75">
                <Workflow className="w-4 h-4" /> {lang === 'es' ? 'Demo guiado' : 'Guided walkthrough'}
              </span>
            </div>
          </motion.button>

          {/* Stat strip — reinforces value below the video */}
          <div className="mt-8 grid sm:grid-cols-3 gap-3 sm:gap-5">
            {[
              { k: '90s', v: lang === 'es' ? 'Recorrido del producto' : 'Product walkthrough' },
              { k: '6', v: lang === 'es' ? 'Agentes en acción' : 'Agents in action' },
              { k: 'EN / ES', v: lang === 'es' ? 'Disponible en ambos idiomas' : 'Available in both languages' },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-neutral-200 bg-white px-5 py-4 flex items-center gap-4">
                <span className="text-[#F77F11] text-[22px] font-bold tracking-[-0.02em] leading-none">{s.k}</span>
                <span className="text-neutral-600 text-[13.5px] leading-snug">{s.v}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 lg:py-24 bg-neutral-50/60" data-testid="ai-agents-capabilities">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-12">
            <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-semibold uppercase">{c.keyEyebrow}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-[42px] font-bold text-neutral-900 tracking-[-0.02em] leading-[1.1]">{c.keyTitle}</h2>
            <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed">{c.keyBody}</p>
          </div>

          <div className="grid lg:grid-cols-12 gap-6">
            {/* Capabilities — icon grid */}
            <div className="lg:col-span-7 rounded-3xl border border-neutral-200 bg-white p-8 sm:p-10">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-bold uppercase">{c.capabilitiesTitle}</p>
                <p className="text-[11px] tracking-[0.18em] uppercase font-semibold text-neutral-400">{lang === 'es' ? '10 tareas, todos los días' : '10 tasks, every day'}</p>
              </div>
              <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {c.capabilities.map((cap, i) => {
                  const I = cap.i;
                  return (
                    <motion.div
                      key={cap.t}
                      initial={{ opacity: 0, y: 8 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: '-40px' }}
                      transition={{ duration: 0.35, delay: i * 0.03 }}
                      className="group flex items-center gap-3 rounded-xl border border-neutral-200 bg-white px-3.5 py-3 hover:border-[#F77F11] hover:bg-orange-50/40 transition-all duration-300"
                    >
                      <span className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center shrink-0 group-hover:bg-[#F77F11] transition-colors duration-300">
                        <I className="w-4 h-4 text-[#F77F11] group-hover:text-white transition-colors duration-300" />
                      </span>
                      <p className="text-[13.5px] font-medium text-neutral-800 leading-snug">{cap.t}</p>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Benefits — dark card */}
            <div className="lg:col-span-5 rounded-3xl bg-[#0F1117] text-white p-8 sm:p-10 relative overflow-hidden">
              <div
                aria-hidden="true"
                className="absolute -top-24 -right-24 w-[280px] h-[280px] rounded-full opacity-30 pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(247,127,17,0.4), transparent 65%)' }}
              />
              <div className="relative">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-[#FFB672] text-[11px] tracking-[0.22em] font-bold uppercase">{c.benefitsTitle}</p>
                  <p className="text-[11px] tracking-[0.18em] uppercase font-semibold text-white/40">{lang === 'es' ? 'Lo que cambia' : 'What changes'}</p>
                </div>
                <ul className="mt-7 grid grid-cols-1 gap-3">
                  {c.benefits.map((b, i) => {
                    const I = b.i;
                    return (
                      <motion.li
                        key={b.t}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: '-40px' }}
                        transition={{ duration: 0.35, delay: i * 0.04 }}
                        className="flex items-center gap-3 text-[14px] rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-2.5 hover:border-[#F77F11]/40 hover:bg-white/[0.07] transition-colors duration-300"
                      >
                        <span className="w-8 h-8 rounded-lg bg-[#F77F11]/15 border border-[#F77F11]/30 flex items-center justify-center shrink-0">
                          <I className="w-4 h-4 text-[#F77F11]" />
                        </span>
                        <span className="font-medium">{b.t}</span>
                      </motion.li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== 6. Managed infrastructure + Trust ecosystem ========== */}
      <section className="py-20 lg:py-24 bg-gradient-to-br from-[#0F1117] via-[#161A22] to-[#0F1117] text-white" data-testid="ai-agents-infrastructure">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 text-white/90 text-[11px] tracking-[0.22em] font-semibold uppercase px-3 py-1.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-[#F77F11]" />
              {c.marblismEyebrow}
            </span>
            <h2 className="mt-6 text-3xl sm:text-4xl lg:text-[42px] font-bold tracking-[-0.02em] leading-[1.1]">{c.marblismTitle}</h2>
            <p className="mt-5 text-neutral-300 text-[15.5px] leading-relaxed max-w-xl">{c.marblismBody}</p>
            <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {c.marblismBullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-[14px] text-neutral-200">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-[#F77F11]" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <Link
                to="/how-it-works"
                className="inline-flex items-center gap-1 text-[#FFB672] hover:text-[#F77F11] font-semibold text-sm group"
                data-testid="ai-infrastructure-cta"
              >
                {c.marblismCta}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a href={MARBLISM_URL} target="_blank" rel="sponsored noopener noreferrer"
                data-testid="ai-infrastructure-marblism-link"
                onClick={() => trackEvent('partner_click', { kind: 'marblism', url: MARBLISM_URL })}
                className="inline-flex items-center gap-1 text-white/50 hover:text-white font-semibold text-sm group">
                {lang === 'es' ? 'Con tecnología Marblism' : 'Powered by Marblism'}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </div>

          {/* Glassy Kitxens infrastructure card */}
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/10 backdrop-blur-md p-8 sm:p-10 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.6)]">
              <p className="text-[10px] tracking-[0.3em] font-semibold uppercase text-white/40">
                {lang === 'es' ? 'Infraestructura gestionada' : 'Managed infrastructure'}
              </p>
              <div className="mt-5 flex items-center gap-4">
                <span className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F77F11] to-[#ff9b4a] flex items-center justify-center shadow-[0_20px_50px_-20px_rgba(247,127,17,0.7)]">
                  <span className="text-white font-bold text-2xl tracking-[-0.02em]">K</span>
                </span>
                <div>
                  <p className="text-[11px] tracking-[0.22em] font-semibold uppercase text-white/60">KITXENS</p>
                  <p className="text-white text-xl font-semibold tracking-[-0.01em]">{lang === 'es' ? 'Capa de Operaciones IA' : 'AI Operations Layer'}</p>
                </div>
              </div>
              <div className="mt-7 grid grid-cols-2 gap-4">
                <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                  <p className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/40">{lang === 'es' ? 'Despliegue' : 'Deploy'}</p>
                  <p className="mt-2 text-white font-semibold text-[15.5px]">{lang === 'es' ? 'En días' : 'In days'}</p>
                </div>
                <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                  <p className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/40">{lang === 'es' ? 'Monitoreo' : 'Monitoring'}</p>
                  <p className="mt-2 text-white font-semibold text-[15.5px]">24/7</p>
                </div>
                <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                  <p className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/40">{lang === 'es' ? 'Seguridad' : 'Security'}</p>
                  <p className="mt-2 text-white font-semibold text-[15.5px]">Enterprise</p>
                </div>
                <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                  <p className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/40">{lang === 'es' ? 'Escala' : 'Scale'}</p>
                  <p className="mt-2 text-white font-semibold text-[15.5px]">{lang === 'es' ? 'Sin límite' : 'Unlimited'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trusted ecosystem strip — tasteful text badges (not a logo wall) */}
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10 mt-14 lg:mt-16">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-7">
              <p className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/45 shrink-0 sm:border-r sm:border-white/10 sm:pr-7">
                {c.partnersEyebrow}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {ECOSYSTEM.map((name) => (
                  <span
                    key={name}
                    className="text-[12.5px] font-semibold text-white/75 hover:text-white bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-[#F77F11]/40 rounded-full px-3.5 py-1.5 transition-all duration-300"
                    data-testid={`ai-ecosystem-${name.toLowerCase()}`}
                  >
                    {name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-20 lg:py-24" data-testid="ai-agents-outcomes">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-semibold uppercase">{c.outcomesEyebrow}</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-neutral-900 tracking-[-0.02em] leading-[1.1] max-w-2xl">
            {lang === 'es' ? 'Resultados que un equipo IA entrega.' : 'Outcomes an AI team actually delivers.'}
          </h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {c.outcomes.map((o, i) => (
              <motion.div
                key={o.k}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="rounded-2xl border border-neutral-200 bg-white p-7 hover:border-orange-200 hover:shadow-[0_24px_60px_-30px_rgba(247,127,17,0.35)] transition-all duration-300"
              >
                <p className="text-[#F77F11] text-4xl sm:text-5xl font-bold tracking-[-0.025em] leading-none">{o.k}</p>
                <p className="mt-4 text-neutral-600 text-[14.5px] leading-relaxed">{o.v}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== 6.5 AI executives sign the webzine ========== */}
      <AuthorsShowcase lang={lang} />

      {/* ========== 8.5. Bridge to the wider Operating Team ========== */}
      <section className="py-14 lg:py-16 bg-white border-t border-neutral-200" data-testid="ai-agents-team-bridge">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <Link
            to="/operating-intelligence"
            className="group flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 rounded-2xl border border-neutral-200 bg-neutral-50/60 hover:bg-orange-50/40 hover:border-[#F77F11] hover:shadow-[0_24px_60px_-30px_rgba(247,127,17,0.35)] p-5 sm:p-6 transition-all"
            data-testid="ai-agents-team-bridge-link"
          >
            <div className="flex items-center gap-4 min-w-0">
              <span className="flex items-center -space-x-2 shrink-0">
                {TEAM.map((m, i) => (
                  <span
                    key={m.key}
                    className="relative inline-flex w-9 h-9 sm:w-10 sm:h-10 rounded-full overflow-hidden ring-2 ring-white bg-white"
                    style={{ zIndex: TEAM.length - i }}
                    title={m.name}
                  >
                    <img src={m.img} alt={m.name} loading="lazy" className="w-full h-full object-cover" />
                  </span>
                ))}
              </span>
              <div className="min-w-0">
                <p className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-[#F77F11]">
                  {lang === 'es' ? 'EL EQUIPO COMPLETO' : 'THE FULL OPERATING TEAM'}
                </p>
                <p className="mt-1.5 text-neutral-900 text-base sm:text-lg font-semibold tracking-[-0.01em] leading-snug">
                  {lang === 'es'
                    ? 'Conoce a los seis especialistas operando detrás de Kitxens.'
                    : 'Meet the six specialists operating behind Kitxens.'}
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#F77F11] group-hover:gap-2.5 transition-all shrink-0">
              {lang === 'es' ? 'Ver Operating Team' : 'See Operating Team'}
              <ArrowUpRight className="w-4 h-4" />
            </span>
          </Link>
        </div>
      </section>

      {/* ========== Pricing funnel ========== */}
      <ProductPricingCTA product="workforce" />

      {/* ========== 9. Final CTA ========== */}
      <section className="py-20 lg:py-24 bg-[#0F1117] text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-[-0.015em]">{c.ctaTitle}</h2>
          <p className="mt-5 text-neutral-300 text-[15.5px] leading-relaxed">{c.ctaBody}</p>
          <div className="mt-9 flex flex-wrap gap-3 justify-center">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-1 bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 px-7 rounded-full text-[15px] font-semibold shadow-sm hover:shadow-md transition-all"
              data-testid="ai-agents-bottom-cta-primary"
            >
              {c.ctaA} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact#contact-form"
              className="inline-flex items-center gap-2 border border-neutral-700 text-neutral-200 hover:border-[#F77F11] hover:text-[#F77F11] px-6 h-12 rounded-full text-[14px] font-semibold transition-colors"
              data-testid="ai-agents-bottom-cta-secondary"
            >
              {c.ctaB}
            </Link>
          </div>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-[12.5px] text-neutral-400">
            <a href="https://wa.me/14085901102" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-[#F77F11]"><MessageCircle className="w-4 h-4 text-[#F77F11]" /> WhatsApp</a>
            <a href="tel:+14155982310" className="inline-flex items-center gap-2 hover:text-[#F77F11]"><Phone className="w-4 h-4 text-[#F77F11]" /> +1 (415) 598-2310</a>
            <a href="mailto:hello@kitxens.com" className="inline-flex items-center gap-2 hover:text-[#F77F11]"><Mail className="w-4 h-4 text-[#F77F11]" /> hello@kitxens.com</a>
          </div>
        </div>
      </section>

      {/* ========== Video Modal ========== */}
      <AnimatePresence>
        {videoOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] bg-black/85 backdrop-blur flex items-center justify-center p-4"
            onClick={() => setVideoOpen(false)}
            data-testid="ai-agents-video-modal"
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="relative w-full max-w-5xl aspect-video bg-black rounded-2xl overflow-hidden shadow-[0_60px_120px_-20px_rgba(0,0,0,0.6)]"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setVideoOpen(false)}
                className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-[#F77F11] text-white flex items-center justify-center transition-colors"
                aria-label="Close video"
                data-testid="ai-agents-video-close"
              >
                <XIcon className="w-4 h-4" />
              </button>
              <video
                src="/media/agents-demo.mp4"
                autoPlay
                controls
                playsInline
                className="absolute inset-0 w-full h-full"
                data-testid="ai-agents-video-player"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <FlagshipStrip current="workforce" />
      <AffiliateStrip className="py-14" />
      <ContinueExploring />
    </div>
  );
}
